import * as t from 'io-ts';
import {memoize} from 'lodash';

import {RefType} from '../../models/api/apiTypes';
import {ResourceMapType} from '../../store/appTypes';

/*
 * Path.
 */

export function extractValueAtPath(path: ReadonlyArray<string>, obj: any): any {
  if (!obj) {
    return obj;
  }

  if (path.length === 0) {
    return obj;
  }

  const [propertyName, ...remainingPath] = path;

  return extractValueAtPath(remainingPath, obj[propertyName]);
}

/*
 * Type Search.
 */

/** For a given ResourceMap type, find its value type. */
export function findResourceMapValueType(resourceMapType: t.Type<any>) {
  if (!(resourceMapType instanceof t.ReadonlyType)) {
    throw new Error('Invalid resource map.');
  }

  const dictionaryType = resourceMapType.type;
  if (!(dictionaryType instanceof t.DictionaryType)) {
    throw new Error('Invalid resource map.');
  }

  const unionType = dictionaryType.codomain;
  if (!(unionType instanceof t.UnionType)) {
    throw new Error('Invalid resource map.');
  }

  const resourceMapValueType = unionType.types[1];
  if (!resourceMapValueType) {
    throw new Error('Invalid resource map.');
  }

  return resourceMapValueType;
}

type TypePredicate = (type: t.Type<any>) => boolean;

function isRefType(type: t.Type<any>) {
  return type instanceof RefType;
}

function isResourceMapType(type: t.Type<any>) {
  return type instanceof ResourceMapType;
}

export const doesTypeContainRefMemoized: TypePredicate = memoize((type: t.Type<any>) =>
  doesTypeContainType(type, isRefType, doesTypeContainRefMemoized),
);
export const doesTypeContainResourceMapMemoized: TypePredicate = memoize((type: t.Type<any>) =>
  doesTypeContainType(type, isResourceMapType, doesTypeContainResourceMapMemoized),
);

function doesTypeContainType(
  type: t.Type<any>,
  isTypeTarget: TypePredicate,
  searchRecursively: TypePredicate,
): boolean {
  if (isTypeTarget(type)) {
    return true;
  }

  // Resource map.
  if (type instanceof ResourceMapType) {
    return searchRecursively(findResourceMapValueType(type));
  }

  // Wrapper types and array types.
  if (
    type instanceof t.RefinementType ||
    type instanceof t.ReadonlyType ||
    type instanceof t.ArrayType ||
    type instanceof t.ReadonlyArrayType
  ) {
    return searchRecursively(type.type);
  }

  // Dictionary type.
  if (type instanceof t.DictionaryType) {
    return searchRecursively(type.codomain);
  }

  // Tagged union, union, and interaction types.
  if (
    type instanceof t.TaggedUnionType ||
    type instanceof t.UnionType ||
    type instanceof t.IntersectionType
  ) {
    return type.types.some((subType: t.Type<any>) => searchRecursively(subType));
  }

  // Interface type.
  if (type instanceof t.InterfaceType || type instanceof t.PartialType) {
    return Object.keys(type.props).some((key) => {
      const propType: t.Type<any> = type.props[key];
      return searchRecursively(propType);
    });
  }

  return false;
}
